import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, ShieldAlert } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { getApiErrorMessage } from "@/lib/api";
import { formatDateTimeSP } from "@/lib/formatDate";
import {
  getManifestationHistory,
  getManifestationPreview,
  manifest,
  reconcileManifestation,
  MANIFESTATION_EVENTS,
  type ManifestationEvent,
  type ManifestationPreview,
  type ManifestationStatus,
} from "@/lib/adminDfeManifestationApi";

// Cada evento traz uma explicação curta (NT 2020.001) exibida em tooltip ao passar o mouse — o significado
// definitivo ainda vem na pré-visualização antes de enviar; aqui é só para distinguir as opções sem erro.
const EVENTS: { tp: string; label: string; help: string }[] = [
  {
    tp: MANIFESTATION_EVENTS.CIENCIA,
    label: "Ciência da Operação",
    help: "Declara à SEFAZ que você TOMOU CONHECIMENTO desta NF-e emitida contra o seu CNPJ. Não concorda nem discorda da operação — apenas registra ciência. Não é conclusivo.",
  },
  {
    tp: MANIFESTATION_EVENTS.CONFIRMACAO,
    label: "Confirmação da Operação",
    help: "Confirma que a operação descrita na NF-e ocorreu e a mercadoria foi recebida. Evento CONCLUSIVO positivo; depois dele o emitente não pode mais cancelar a NF-e.",
  },
  {
    tp: MANIFESTATION_EVENTS.DESCONHECIMENTO,
    label: "Desconhecimento da Operação",
    help: "Declara que você NÃO RECONHECE a operação registrada nesta NF-e contra o seu CNPJ (possível uso indevido do cadastro). Evento CONCLUSIVO.",
  },
  {
    tp: MANIFESTATION_EVENTS.NAO_REALIZADA,
    label: "Operação não Realizada",
    help: "A operação era do seu conhecimento, mas NÃO SE CONCRETIZOU (ex.: recusa/devolução total da mercadoria). Evento CONCLUSIVO; exige justificativa.",
  },
];

const STATUS_BADGE: Record<ManifestationStatus, "default" | "secondary" | "outline" | "destructive"> = {
  ACEITO: "default",
  PENDENTE: "secondary",
  INCERTO: "outline",
  REJEITADO: "destructive",
};

/**
 * Manifestação do Destinatário (NT 2020.001) — painel EXCLUSIVO do ADMIN. SEMPRE manual: cada evento abre uma
 * pré-visualização (significado + dados da NF + prazos) e exige confirmação explícita. Histórico PRÓPRIO; é
 * separado por completo da Validação Naturexpress (conferência interna). Nunca anuncia sucesso antes do aceite.
 */
export function SupplierInvoiceManifestationPanel({ accessKey }: { accessKey: string }) {
  const [history, setHistory] = useState<ManifestationEvent[]>([]);
  const [preview, setPreview] = useState<ManifestationPreview | null>(null);
  const [open, setOpen] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [justificativa, setJustificativa] = useState("");
  const [sending, setSending] = useState(false);
  const requestIdRef = useRef<string | null>(null);

  const loadHistory = () => {
    getManifestationHistory(accessKey).then(setHistory).catch(() => setHistory([]));
  };
  useEffect(() => {
    loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessKey]);

  const openPreview = async (tpEvento: string) => {
    setLoadingPreview(true);
    setJustificativa("");
    requestIdRef.current = crypto.randomUUID();
    try {
      const p = await getManifestationPreview(accessKey, tpEvento);
      setPreview(p);
      setOpen(true);
    } catch (e) {
      toast.error(getApiErrorMessage(e, "Falha ao carregar a pré-visualização"));
    } finally {
      setLoadingPreview(false);
    }
  };

  const send = async () => {
    if (!preview || !requestIdRef.current) return;
    setSending(true);
    try {
      const r = await manifest({
        accessKey,
        tpEvento: preview.tpEvento,
        justificativa: preview.requiresJustification ? justificativa.trim() : undefined,
        requestId: requestIdRef.current,
      });
      // Só anuncia sucesso quando a SEFAZ ACEITOU; os demais desfechos são avisos/erros explícitos.
      if (r.outcome === "OK") {
        toast.success(`Manifestação registrada${r.event?.protocolo ? ` (protocolo ${r.event.protocolo})` : ""}.`);
        setOpen(false);
      } else if (r.outcome === "UNCERTAIN") {
        toast.warning("Resultado INCERTO (timeout/rede). Nada será retransmitido automaticamente; decida uma nova tentativa manualmente.");
        setOpen(false);
      } else {
        toast.warning(r.message);
      }
      loadHistory();
    } catch (e) {
      toast.error(getApiErrorMessage(e, "Falha ao manifestar"));
    } finally {
      setSending(false);
    }
  };

  // Conciliação MANUAL de um evento INCERTO (ADMIN) com base em verificação oficial externa — SEM consulta
  // automática. Referência obrigatória (operador/data são registrados no backend); preserva o registro original.
  const reconcile = async (eventId: string, registered: boolean) => {
    const reference = window.prompt(
      "Referência da verificação OFICIAL (obrigatória) — ex.: consulta/protocolo no portal da SEFAZ em DD/MM/AAAA:");
    if (reference === null) return; // operador cancelou
    if (reference.trim().length < 3) {
      toast.error("Referência da verificação oficial é obrigatória.");
      return;
    }
    const protocolo = registered
      ? (window.prompt("Protocolo verificado na SEFAZ (opcional):") ?? "").trim() || undefined
      : undefined;
    try {
      await reconcileManifestation({ eventId, registered, reference: reference.trim(), protocolo });
      toast.success(registered ? "Conciliado como registrado." : "Conciliado como não registrado.");
      loadHistory();
    } catch (e) {
      toast.error(getApiErrorMessage(e, "Falha ao conciliar"));
    }
  };

  const justInvalid = !!preview?.requiresJustification
    && (justificativa.trim().length < 15 || justificativa.trim().length > 255);

  return (
    <div className="rounded-xl border p-4 text-sm">
      <div className="mb-2 flex items-center gap-2">
        <ShieldAlert className="h-4 w-4 text-muted-foreground" />
        <h3 className="font-semibold">Manifestação do destinatário (SEFAZ)</h3>
      </div>
      <p className="mb-3 text-xs text-muted-foreground">
        Evento fiscal da SEFAZ, <strong>separado</strong> da Validação Naturexpress (conferência interna). Sempre
        manual e exclusivo do ADMIN. A ausência de registro aqui não comprova ausência de manifestação na SEFAZ.
      </p>

      <TooltipProvider delayDuration={200}>
        <div className="flex flex-wrap gap-2">
          {EVENTS.map((e) => (
            <Tooltip key={e.tp}>
              <TooltipTrigger asChild>
                <Button size="sm" variant="outline" disabled={loadingPreview}
                  title={e.help}
                  onClick={() => void openPreview(e.tp)}>
                  {e.label}
                </Button>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs text-xs leading-snug">{e.help}</TooltipContent>
            </Tooltip>
          ))}
        </div>
      </TooltipProvider>

      <div className="mt-4">
        <div className="mb-1 text-xs font-medium text-muted-foreground">Histórico de eventos</div>
        {history.length === 0 ? (
          <p className="text-xs text-muted-foreground">Sem manifestação conhecida localmente para esta nota.</p>
        ) : (
          <ul className="space-y-1">
            {history.map((h) => (
              <li key={h.id} className="flex flex-wrap items-center gap-2 border-b border-border/60 py-1">
                <Badge variant={STATUS_BADGE[h.status]}>{h.status}</Badge>
                <span className="font-medium">{h.label}</span>
                <span className="text-xs text-muted-foreground">#{h.nSeqEvento}</span>
                {h.protocolo && <span className="text-xs">protocolo {h.protocolo}</span>}
                {h.cStat && <span className="text-xs text-muted-foreground">cStat {h.cStat}</span>}
                <span className="ml-auto text-xs text-muted-foreground">{formatDateTimeSP(h.createdAt)}</span>
                {h.xMotivo && <span className="w-full text-xs text-muted-foreground">{h.xMotivo}</span>}
                {h.reconcileReference && (
                  <span className="w-full text-xs text-muted-foreground">
                    Conciliado{h.reconciledAt ? ` em ${formatDateTimeSP(h.reconciledAt)}` : ""} · ref.: {h.reconcileReference}
                  </span>
                )}
                {h.status === "INCERTO" && (
                  <span className="flex w-full items-center gap-2 text-xs">
                    <span className="text-amber-700">Concilie (verifique em fonte oficial):</span>
                    <Button size="sm" variant="outline" className="h-6 px-2" onClick={() => void reconcile(h.id, true)}>
                      Registrado
                    </Button>
                    <Button size="sm" variant="outline" className="h-6 px-2" onClick={() => void reconcile(h.id, false)}>
                      Não registrado
                    </Button>
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      <Dialog open={open} onOpenChange={(o) => { if (!o && !sending) setOpen(false); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{preview?.label}</DialogTitle>
            <DialogDescription>{preview?.meaning}</DialogDescription>
          </DialogHeader>
          {preview && (
            <div className="space-y-2 text-sm">
              <div className="rounded-md border bg-muted/40 p-3 text-xs">
                <div>Fornecedor: <strong>{preview.emitterName ?? "—"}</strong> ({preview.emitterDocument ?? "—"})</div>
                <div>NF nº <strong>{preview.nNf ?? "—"}</strong> · valor <strong>{preview.totalNfe ?? "—"}</strong></div>
                <div className="break-all">Chave: {preview.accessKey}</div>
                {preview.conclusive && preview.conclusiveDeadlineDays != null && (
                  <div>Prazo conclusivo{preview.deadlineEstimated ? " (estimativa)" : ""}: <strong>{preview.conclusiveDeadlineDays}</strong> dia(s) {preview.conclusiveDeadlineDays < 0 ? "estimados além do prazo" : "estimados restantes"}</div>
                )}
                {!preview.conclusive && preview.cienciaDeadlineDays != null && (
                  <div>Prazo da ciência{preview.deadlineEstimated ? " (estimativa)" : ""}: <strong>{preview.cienciaDeadlineDays}</strong> dia(s) {preview.cienciaDeadlineDays < 0 ? "estimados além do prazo" : "estimados restantes"}</div>
                )}
                {preview.deadlineEstimated && (
                  <div className="text-muted-foreground">Prazo é ESTIMATIVA (base emissão; autorização indisponível) — não é vencimento legal.</div>
                )}
                {preview.cienciaKnownViaDistribution && (
                  <div className="text-muted-foreground">Ciência já conhecida pela distribuição DF-e.</div>
                )}
                {preview.conclusive && !preview.countAuthoritative && (
                  <div className="text-amber-700">Contagem reflete o que é CONHECIDO pela distribuição até o último NSU — não é o histórico fiscal completo.</div>
                )}
                {preview.knownUpToNsu != null && (
                  <div className="text-muted-foreground">
                    Conhecido até NSU <strong>{preview.knownUpToNsu}</strong>
                    {preview.knownUpToAt ? ` (${formatDateTimeSP(preview.knownUpToAt)})` : ""} — chegar ao fim da fila não prova histórico completo.
                  </div>
                )}
                {preview.receivedEvents && preview.receivedEvents.length > 0 && (
                  <div className="rounded-md border bg-muted/30 p-2 text-xs">
                    <div className="font-medium">Eventos conhecidos pela distribuição (evidência)</div>
                    <ul className="mt-1 space-y-0.5">
                      {preview.receivedEvents.map((r) => (
                        <li key={r.nsu}>
                          {r.label} · #{r.nSeqEvento} · autor {r.authorDocument ?? "—"}
                          {r.protocolo ? ` · protocolo ${r.protocolo}` : ""} · NSU {r.nsu} · {r.format}
                        </li>
                      ))}
                    </ul>
                    <p className="mt-1 text-muted-foreground">Não provam o nosso envio específico (o mesmo CNPJ pode ser usado por outro sistema).</p>
                  </div>
                )}
              </div>

              {preview.requiresJustification && (
                <div className="flex flex-col gap-1">
                  <Label htmlFor="man-just">Justificativa (15 a 255 caracteres)</Label>
                  <Input id="man-just" value={justificativa} maxLength={255}
                    onChange={(e) => setJustificativa(e.target.value)}
                    placeholder="Descreva por que a operação não se realizou" />
                </div>
              )}

              {preview.hasUnresolvedUncertain && (
                <p className="rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900">
                  Há manifestação anterior com resultado <strong>INCERTO</strong>. Marcar ciência do risco NÃO libera
                  reenvio: <strong>concilie o evento INCERTO no histórico</strong> (confirme em fonte oficial se foi ou
                  não registrado) antes de nova manifestação. Nada é retransmitido automaticamente.
                </p>
              )}

              {!preview.allowed && !preview.hasUnresolvedUncertain && (
                <p className="text-xs text-destructive">{preview.blockReason}</p>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} disabled={sending}>Cancelar</Button>
            <Button
              onClick={() => void send()}
              disabled={sending || justInvalid || (!!preview && !preview.allowed)}
            >
              {sending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
              Confirmar e manifestar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
